/**
 * The one dropdown the Skills pane's controls are built from — its own menu, not the
 * platform's.
 *
 * A native `<select>` was here first, and for keyboard behaviour it was the better
 * component. It lost on the two things the owner asked for and the platform will not give:
 *
 * - **The options cannot be styled.** The popup is drawn by the OS, so a status marker had
 *   to be a word in the label (`2024 · open`) because a span is stripped and a `●` inherits
 *   the option's own black. Here an option is markup: the label, a quiet hint on the right,
 *   and a check on the one in force.
 * - **The closed width is the widest option's, not the value's.** "AutoCAD — coming soon"
 *   set the width of a control whose value reads "Revit", which is what made the heading's
 *   vendor control look oversized. Here the trigger is sized by its own text and the menu
 *   sizes itself to the options (`width: max-content`), so the two are independent.
 *
 * What has to be re-earned in exchange is the keyboard, which is done below: the arrows,
 * Home/End, Escape back to the trigger, and a Tab that closes rather than walking into a
 * menu that is visually gone. Type-ahead is the one native behaviour not reproduced — with
 * at most a handful of short options it buys little, and getting it subtly wrong is worse
 * than not having it.
 *
 * Two variants, because both are the same control in different clothes: `pill` for the
 * filter and sort controls in the list head, `title` for the vendor inside the `h2`.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CheckIcon, ChevronDownIcon } from "./icons";

export interface DropdownOption<T extends string> {
  value: T;
  /** What the trigger shows when this one is in force, and the option's own first line. */
  label: string;
  /** Quiet text at the right of the option — a state ("open") or a caveat ("coming soon"). */
  hint?: string;
  disabled?: boolean;
}

export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  label,
  variant,
  glyph,
  disabled = false,
}: {
  value: T;
  options: readonly DropdownOption<T>[];
  onChange: (next: T) => void;
  /** The accessible name and the tooltip — the visible label is the value itself. */
  label: string;
  variant: "pill" | "title";
  /** Leading icon, inside the trigger. The pills carry one; the title does not. */
  glyph?: React.ReactNode;
  /**
   * **Read-only: the value is shown, the menu never opens.**
   *
   * Kept as the same component rather than swapped for a plain span (owner), so the day the
   * value becomes selectable again is one prop rather than a rewrite. **The chevron goes
   * with the behaviour, though**: a caret is a promise that something opens, and one that
   * never does is worse than none — it reads as a control that is failing to respond. The
   * pill's frame stays, because the value is still a scope and not a sentence.
   *
   * `aria-disabled` rather than the `disabled` attribute, so the trigger keeps its place in
   * the tab order and a keyboard or screen-reader user can still reach the name and the
   * tooltip that say what sets it. `aria-haspopup` and `aria-expanded` come off with it —
   * there is no menu to announce.
   */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  /** Opening upwards, because there is no room below — see the effect that decides it. */
  const [up, setUp] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  /**
   * **Flip above the trigger when the menu would not fit below it.**
   *
   * The page-size control sits in the footer, at the bottom of the pane, and `.app-content`
   * is an `overflow: hidden` box — so a menu opening downwards from there was not merely
   * awkward, it was clipped, and the options could not be reached at all.
   *
   * Measured rather than assumed: a layout effect runs before paint, so the menu is rendered,
   * its real height read, and the position corrected without a visible jump. It flips only
   * when there is more room above than below, so a control near the top of a short window
   * still opens downwards instead of into even less space.
   */
  useLayoutEffect(() => {
    if (!open) {
      setUp(false);
      return;
    }
    const trigger = triggerRef.current?.getBoundingClientRect();
    const menu = menuRef.current?.getBoundingClientRect();
    if (!trigger || !menu) return;
    const gap = 5;
    const below = window.innerHeight - trigger.bottom - gap;
    const above = trigger.top - gap;
    setUp(menu.height > below && above > below);
  }, [open]);

  /**
   * Focus lands in the menu when it opens — on the option in force, so the arrows move from
   * where the eye already is. A menu that opens without focus leaves the keyboard on the
   * trigger, where ArrowDown would open it a second time.
   */
  useEffect(() => {
    if (!open) return;
    const items = enabledItems(menuRef.current);
    const current = items.find((el) => el.dataset["value"] === value);
    (current ?? items[0])?.focus();
  }, [open, value]);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = enabledItems(menuRef.current);
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      // Wraps, as a menu of a few items should: the ends are not walls to discover.
      items[(at + step + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
      close(true);
    } else if (e.key === "Tab") {
      // Let the browser move on, but not into a menu the user has left behind.
      setOpen(false);
    }
  };

  return (
    <div className={`dd is-${variant}${disabled ? " is-readonly" : ""}`} ref={root}>
      <button
        ref={triggerRef}
        type="button"
        className="dd-trigger"
        {...(disabled ? { "aria-disabled": true } : { "aria-haspopup": "menu" as const, "aria-expanded": open })}
        aria-label={label}
        title={label}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {glyph ? <span className="dd-glyph" aria-hidden="true">{glyph}</span> : null}
        <span className="dd-value">{selected?.label ?? ""}</span>
        {/**
         * **The title variant has no caret at all** (owner) — not hidden, not reserved.
         *
         * It was in flow with `visibility: hidden` so that revealing it on hover would not
         * nudge "Skills" sideways, but reserving a chevron's width inside a two-word phrase
         * is the same problem in quieter form: the gap between "Revit" and "Skills" read as
         * a missing glyph rather than as a space. Gone, the words sit a word apart. What
         * says the vendor is a control is the dotted underline at rest and the frame on
         * hover.
         */}
        {variant === "pill" && !disabled ? (
          <span className="dd-caret" aria-hidden="true"><ChevronDownIcon size={12} /></span>
        ) : null}
      </button>
      {open && !disabled ? (
        <div className={`dd-menu${up ? " is-up" : ""}`} role="menu" aria-label={label} ref={menuRef} onKeyDown={onMenuKey}>
          {options.map((option) => {
            const active = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                // `tabIndex` so the arrows own the movement inside the menu, not Tab.
                tabIndex={-1}
                data-value={option.value}
                className={`dd-opt${active ? " is-active" : ""}`}
                disabled={option.disabled ?? false}
                onClick={() => {
                  onChange(option.value);
                  close(true);
                }}
              >
                <span className="dd-opt-label">{option.label}</span>
                {option.hint ? <span className="dd-opt-hint">{option.hint}</span> : null}
                {/* In flow either way, so the labels of a menu do not shift by a
                    tick's width between the checked row and the rest. */}
                <span className="dd-opt-check" aria-hidden="true">{active ? <CheckIcon size={13} /> : null}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/** The options a keypress may move to — a disabled one is skipped, not stopped on. */
function enabledItems(menu: HTMLDivElement | null): HTMLButtonElement[] {
  if (!menu) return [];
  return Array.from(menu.querySelectorAll<HTMLButtonElement>(".dd-opt:not(:disabled)"));
}
